import React from "react";
import { useSelector, useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import { useTheme, alpha } from "@mui/material/styles";
import { closeOrderLimitModal } from "../../store/actions/ordersSlice";

/**
 * Global modal shown when a dispatch is blocked because the plan's monthly
 * order-dispatch quota (max_orders_per_month) has been reached. Renders the
 * specific pt-BR message from the backend and a link to upgrade the plan.
 *
 * Driven by redux state `orders.orderLimitModal`, set in the
 * `dispatchOrders.rejected` reducer.
 */
export default function OrderLimitModal() {
  const theme = useTheme();
  const dispatch = useDispatch();
  const history = useHistory();

  const { open, message, details } = useSelector(
    (state) => state.orders.orderLimitModal || {}
  );

  const handleClose = () => dispatch(closeOrderLimitModal());

  const handleUpgrade = () => {
    dispatch(closeOrderLimitModal());
    history.push("/minha-assinatura");
  };

  return (
    <Dialog
      open={!!open}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}
    >
      <Box
        sx={{
          p: 3,
          pb: 2,
          textAlign: "center",
          bgcolor: alpha(theme.palette.warning.main, 0.12),
          position: "relative",
        }}
      >
        <IconButton
          onClick={handleClose}
          size="small"
          sx={{ position: "absolute", top: 8, right: 8, color: "text.secondary" }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
        <Box
          sx={{
            width: 56,
            height: 56,
            mx: "auto",
            mb: 1.5,
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: theme.palette.warning.main,
            color: theme.palette.warning.contrastText,
          }}
        >
          <RocketLaunchIcon />
        </Box>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          Limite de pedidos atingido
        </Typography>
      </Box>

      <DialogContent sx={{ pt: 2.5, pb: 1 }}>
        <Typography variant="body2" color="text.secondary" align="center">
          {message ||
            "Você atingiu o limite de pedidos despachados do seu plano neste mês."}
        </Typography>
        {details?.limit != null && (
          <Typography
            variant="body2"
            align="center"
            sx={{ mt: 1.5, fontWeight: 600 }}
          >
            {details.current_count} / {details.limit} pedidos neste mês
          </Typography>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, pt: 1, flexDirection: "column", gap: 1 }}>
        <Button
          onClick={handleUpgrade}
          variant="contained"
          color="primary"
          fullWidth
          size="large"
          endIcon={<RocketLaunchIcon />}
          sx={{ borderRadius: 2, fontWeight: 700 }}
        >
          Fazer upgrade do plano
        </Button>
        <Button
          onClick={handleClose}
          color="inherit"
          fullWidth
          sx={{ color: "text.secondary" }}
        >
          Agora não
        </Button>
      </DialogActions>
    </Dialog>
  );
}
